import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Grid, IconButton, InputAdornment } from "@mui/material";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import EditIcon from "@mui/icons-material/Edit";
import {
  FormikLabeledTextField,
} from "../../../components/shared/form/FormikLabeledTextField";
import FormikTerminalAutocomplete, {
  type TerminalWithNameOption,
} from "../../../components/shared/form/FormikTerminalAutocomplete";
import type { MisdeliveryBolFormValues } from "../../../@hooks/useMisdeliveryBolForm";
import type { FormikProps } from "formik";
import { useSelector } from "react-redux";
import { terminalWithNameSelector } from "../../../AppState/selectors";
import BarcodeScannerDialog from "../../../components/shared/barcode/BarcodeScannerDialog";
import { useTerminalMaster } from "../../../@hooks/useTerminalMaster";

/**
 * Performance Optimizations:
 * - Wrapped in React.memo to prevent re-renders when parent state changes
 * - Stable callbacks with proper dependency management
 * - Minimal prop requirements
 */

const NAV_BUTTONS_STYLE = {
  marginTop: "20px",
  justifyContent: "space-between",
};

export interface DriverPickupInformationStepProps {
  formik: FormikProps<MisdeliveryBolFormValues>;
  onBack: () => void;
  formFieldsDisabled?: boolean;
  terminalReadOnly?: boolean;
  bolProReadOnly?: boolean;
  submitButtonText?: string;
}

const DriverPickupInformationStepComponent: React.FC<
  DriverPickupInformationStepProps
> = ({
  formik,
  onBack,
  formFieldsDisabled = false,
  terminalReadOnly = false,
  bolProReadOnly = false,
  submitButtonText,
}) => {
  const terminalMaster: TerminalWithNameOption[] = useSelector(
    terminalWithNameSelector,
  );

  const [scannerOpen, setScannerOpen] = useState(false);
  const [isProNumberEditable, setIsProNumberEditable] = useState(false);

  const terminalDisabled = formFieldsDisabled || terminalReadOnly;
  const bolProDisabled = formFieldsDisabled || bolProReadOnly;

  const handleScan = useCallback(
    (result: { text: string }) => {
      // Note: Button is disabled when bolProDisabled, so this callback cannot be invoked
      formik.setFieldValue("misdeliveredBolPro", result.text);
      setIsProNumberEditable(false);
    },
    [formik],
  );

  const openScanner = useCallback(() => {
    // Note: Button is disabled when bolProDisabled, so this callback cannot be invoked
    setIsProNumberEditable(false);
    setScannerOpen(true);
  }, []);

  const closeScanner = useCallback(() => {
    setScannerOpen(false);
  }, []);

  const toggleProNumberEdit = useCallback(() => {
    // Note: Button is disabled when bolProDisabled, so this callback cannot be invoked
    setIsProNumberEditable((prev) => {
      const next = !prev;
      if (next) {
        // Focus after enabling the field
        setTimeout(() => {
          document?.getElementsByName("misdeliveredBolPro")?.[0]?.focus();
        }, 0);
      }
      return next;
    });
  }, []);
  const { loadTerminals } = useTerminalMaster();
  useEffect(() => {
    if (terminalMaster.length === 0) {
      loadTerminals();
    }
  }, [terminalMaster, loadTerminals]);

    useEffect(() => {
    const id = "driver-pickup-information";
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, []);

  return (
    <Box p={0}>
    <h3 className="accordion-content-header" id="driver-pickup-information">Driver Pickup Information</h3>

      <Grid container spacing={2} className="filter-margin">
        <FormikTerminalAutocomplete
          formik={formik}
          name="terminalId"
          label="Terminal"
          required
          placeholder="Enter & Select Terminal"
          options={terminalMaster}
          disabled={terminalDisabled}
          setFieldsFromOption={{
            consigneeAddress1: (t) => t.terminalAddress1,
            consigneeAddress2: (t) => t.terminalAddress2,
            consigneeCity: (t) => t.terminalCity,
            consigneeZip: (t) => t.terminalZip,
            consigneeState: (t) => t.terminalState,
          }}
          gridProps={{ md: 3, xs: 12 }}
        />

        <Grid item md={9} xs={12}></Grid>

        <FormikLabeledTextField
          formik={formik}
          name="consigneeName"
          label="Consignee"
          required
          disabled
          maxLength={30}
          gridProps={{ md: 3, xs: 12 }}
        />

        <FormikLabeledTextField
          formik={formik}
          name="consigneeAddress1"
          label="Address 1"
          required
          disabled
          maxLength={30}
          gridProps={{ md: 3, xs: 12 }}
        />

        <FormikLabeledTextField
          formik={formik}
          name="consigneeAddress2"
          label="Address 2"
          // required
          disabled
          maxLength={30}
          gridProps={{ md: 3, xs: 12 }}
        />

        <FormikLabeledTextField
          formik={formik}
          name="consigneeCity"
          label="City"
          required
          disabled
          maxLength={20}
          gridProps={{ md: 3, xs: 12 }}
        />

        <FormikLabeledTextField
          formik={formik}
          name="consigneeState"
          label="State"
          required
          disabled
          maxLength={2}
          gridProps={{ md: 3, xs: 12 }}
        />

        <FormikLabeledTextField
          formik={formik}
          name="consigneeZip"
          label="Zip"
          required
          disabled
          type="number"
          maxLength={10}
          gridProps={{ md: 3, xs: 12 }}
        />
      </Grid>

      <Grid container spacing={2} className="filter-margin">
        <FormikLabeledTextField
          formik={formik}
          name="misdeliveredBolPro"
          label="PRO #"
          placeholder="Enter PRO #"
          required
          type="number"
          maxLength={12}
          gridProps={{ md: 3, xs: 12 }}
          disabled={bolProDisabled || !isProNumberEditable}
          endAdornment={
            <InputAdornment position="end">
              {/* <Box sx={{ display: "flex", gap: 0.5 }}> */}
              <IconButton
                onClick={openScanner}
                edge="end"
                size="small"
                disabled={bolProDisabled}
              >
                <QrCodeScannerIcon />
              </IconButton>
              <IconButton
                aria-label={
                  isProNumberEditable ? "Stop editing PRO #" : "Edit PRO #"
                }
                onClick={toggleProNumberEdit}
                edge="end"
                size="small"
                disabled={bolProDisabled}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              {/* </Box> */}
            </InputAdornment>
          }
        />

      </Grid>

      <Grid container spacing={2} style={NAV_BUTTONS_STYLE}>
        <Grid item>
          <Button variant="outlined" onClick={onBack} className="clear-btn">
            Back
          </Button>
        </Grid>
        <Grid item style={{ marginLeft: 'auto', display: 'flex', gap: '10px' }}>
          {!formFieldsDisabled && (
            <Button variant="contained" type="submit" className="entry">
              {submitButtonText ?? "Submit & Generate BOL"}
            </Button>
          )}
        </Grid>
      </Grid>

      <BarcodeScannerDialog
        open={scannerOpen}
        onClose={closeScanner}
        onScan={handleScan}
      />
    </Box>
  );
};

const DriverPickupInformationStep = React.memo(
  DriverPickupInformationStepComponent,
);
export default DriverPickupInformationStep;
